import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatCard } from '@angular/material/card';
import { MatRadioModule } from '@angular/material/radio';
import { ApplicantPqqSectionsComponent } from '../applicant-pqq-sections.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  FormArray,
  FormControl,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgToastService } from 'ng-angular-popup';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { Router } from '@angular/router';
import { MatExpansionModule } from '@angular/material/expansion';
import { FileManagerComponent } from '@/app/components/shared/file-manager/file-manager.component';

@Component({
  standalone: true,
  imports: [
    MatExpansionModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCard,
    MatRadioModule,
    FileManagerComponent,
  ],
  selector: 'app-sustainbility',
  templateUrl: './sustainbility.component.html',
  styleUrls: ['./sustainbility.component.css'],
})
export class SustainbilityComponent implements OnInit, OnChanges {
  @Input() applicantData: any;
  @Input() isEdit: boolean;
  formGroup: FormGroup;
  @Input() isFetchApplicantData: boolean;
  /**
   * NOTE: this is using the new files manager component. For old files,
   * they will be required to be updated using this component as well
   */
  oldSustainabilityFiles: any[] = [];
  deletedOldSustainabilityFilesIds: any[] = [];
  oldSustainabilityInitiativesFiles: any[] = [];
  deletedOldSustainabilityInitiativesFilesIds: any[] = [];
  oldSocialValuesFiles: any[] = [];
  deletedOldSocialValuesFilesIds: any[] = [];
  oldCorporateResponsibilityFiles: any[] = [];
  deletedOldCorporateResponsibilityFilesIds: any[] = [];

  constructor(
    private fb: FormBuilder,
    private toast: NgToastService,
    private projectsService: ProjectsService,
    private router: Router,
    private ApplicantPqqSectionsComponent: ApplicantPqqSectionsComponent
  ) {}
  ngOnInit(): void {
    this.formGroup = this.fb.group({
      // Sustainability and Social Value

      sustainability_outline: [''],
      sustainability_initiatives_taken: [''],
      social_contributions_community_and_industry: [''],
      corporate_social_initiatives: [''],

      SustainabilityFileControl: new FormArray([]),
      SustainabilityInitiativesFileControl: new FormArray([]),
      SocialValuesFileControl: new FormArray([]),
      CorporateResponsibilityFileControl: new FormArray([]),
    });

    // If it's an edit scenario, patch the form with the applicant data
    if (this.isEdit) {
      this.patchFormValues();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isFetchApplicantData'] &&
      !changes['isFetchApplicantData'].firstChange
    ) {
      this.patchFormValues();
    }
  }
  patchFormValues() {
    this.formGroup.patchValue({
      sustainability_outline: this.applicantData?.sustainability_outline || '',
      sustainability_initiatives_taken:
        this.applicantData?.sustainability_initiatives_taken || '',
      social_contributions_community_and_industry:
        this.applicantData?.social_contributions_community_and_industry || '',
      corporate_social_initiatives:
        this.applicantData?.corporate_social_initiatives || '',
    });

    this.addOldAttachmentMapping(this.applicantData);
  }

  handleBack() {
    this.ApplicantPqqSectionsComponent.previousStep();
  }

  prepareDataToSubmit() {
    const dataToSubmit = new FormData();

    // Dynamically append all form controls to FormData
    Object.keys(this.formGroup.controls).forEach((key) => {
      const value = this.formGroup.get(key)?.value;
      if (value !== null && value !== undefined && value !== '') {
        dataToSubmit.append(key, value);
      }
    });

    // Files handling Start
    this.addUploadedFiles(dataToSubmit);
    this.addDeletedOldFilesIds(dataToSubmit);
    // Files handling End

    return dataToSubmit;
  }

  addUploadedFiles(submissionFormData) {
    // Adding files to FormData
    this.formGroup.value.SustainabilityFileControl.forEach(
      (uploadedFile: File) => {
        if (uploadedFile) {
          submissionFormData.append(
            'sust_construction_project_attach[]',
            uploadedFile
          );
        }
      }
    );
    this.formGroup.value.SustainabilityInitiativesFileControl.forEach(
      (uploadedFile: File) => {
        if (uploadedFile) {
          submissionFormData.append(
            'sust_initiatives_innovations_attach[]',
            uploadedFile
          );
        }
      }
    );
    this.formGroup.value.SocialValuesFileControl.forEach(
      (uploadedFile: File) => {
        if (uploadedFile) {
          submissionFormData.append(
            'company_contributes_attach[]',
            uploadedFile
          );
        }
      }
    );
    this.formGroup.value.CorporateResponsibilityFileControl.forEach(
      (uploadedFile: File) => {
        if (uploadedFile) {
          submissionFormData.append(
            'corporate_social_responsibility_attach[]',
            uploadedFile
          );
        }
      }
    );
  }

  // NOTE: on submit, we append deleted files ids into data
  addDeletedOldFilesIds(submissionFormData) {
    if (this.deletedOldSustainabilityFilesIds?.length > 0) {
      submissionFormData.append(
        'delete_sust_construction_project_attach_ids',
        JSON.stringify(this.deletedOldSustainabilityFilesIds)
      );
    }
    if (this.deletedOldSustainabilityInitiativesFilesIds?.length > 0) {
      submissionFormData.append(
        'delete_sust_initiatives_innovations_attach_ids',
        JSON.stringify(this.deletedOldSustainabilityInitiativesFilesIds)
      );
    }
    if (this.deletedOldSocialValuesFilesIds?.length > 0) {
      submissionFormData.append(
        'delete_company_contributes_attach_ids',
        JSON.stringify(this.deletedOldSocialValuesFilesIds)
      );
    }
    if (this.deletedOldCorporateResponsibilityFilesIds?.length > 0) {
      submissionFormData.append(
        'delete_corporate_social_responsibility_attach_ids',
        JSON.stringify(this.deletedOldCorporateResponsibilityFilesIds)
      );
    }
  }

  onSubmit(): void {
    if (this.formGroup.valid) {
      const dataToSubmit = this.prepareDataToSubmit();
      this.projectsService.projectPQQResponseStep7(dataToSubmit).subscribe({
        next: () => {
          this.toast.success('Pre Qualification submitted', 'SUCCESS');
          this.ApplicantPqqSectionsComponent.nextStep();
        },
        error: (error: any) => {
          console.error('Failed to submit:', error);
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
    } else {
      this.toast.warning('Please fill the required fields', 'WARNING');
      this.formGroup.markAllAsTouched(); // Highlights all invalid fields
    }
  }

  // NOTE: on edit, we add old files to the form array
  addOldAttachmentMapping(response) {
    if (response.sustainability_construction_attach) {
      this.oldSustainabilityFiles =
        response.sustainability_construction_attach || [];
    }
    if (response.sustainability_initiative_attach) {
      this.oldSustainabilityInitiativesFiles =
        response.sustainability_initiative_attach || [];
    }
    if (response.compoany_contributions_attach) {
      this.oldSocialValuesFiles = response.compoany_contributions_attach || [];
    }
    if (response.corporate_social_attach) {
      this.oldCorporateResponsibilityFiles =
        response.corporate_social_attach || [];
    }
  }

  /**
   * Handling File Operations - START
   */
  handleNewFiles(filesData: {
    files: File[];
    controlName: string;
    isEmpty: boolean;
  }) {
    switch (filesData.controlName) {
      case 'SustainabilityFileControl': {
        const fileArray = this.formGroup.get(
          'SustainabilityFileControl'
        ) as FormArray;
        fileArray.clear();

        // If no files are present, we break
        if (filesData.isEmpty) {
          break;
        }

        // Add new files to the form array
        filesData.files.forEach((file) =>
          fileArray.push(new FormControl(file))
        );
        break;
      }
      case 'SustainabilityInitiativesFileControl': {
        const fileArray = this.formGroup.get(
          'SustainabilityInitiativesFileControl'
        ) as FormArray;
        fileArray.clear();

        // If no files are present, we break
        if (filesData.isEmpty) {
          break;
        }

        // Add new files to the form array
        filesData.files.forEach((file) =>
          fileArray.push(new FormControl(file))
        );
        break;
      }
      case 'SocialValuesFileControl': {
        const fileArray = this.formGroup.get(
          'SocialValuesFileControl'
        ) as FormArray;
        fileArray.clear();

        // If no files are present, we break
        if (filesData.isEmpty) {
          break;
        }

        // Add new files to the form array
        filesData.files.forEach((file) =>
          fileArray.push(new FormControl(file))
        );
        break;
      }
      case 'CorporateResponsibilityFileControl': {
        const fileArray = this.formGroup.get(
          'CorporateResponsibilityFileControl'
        ) as FormArray;
        fileArray.clear();

        // If no files are present, we break
        if (filesData.isEmpty) {
          break;
        }

        // Add new files to the form array
        filesData.files.forEach((file) =>
          fileArray.push(new FormControl(file))
        );
        break;
      }
      default:
        break;
    }
  }

  handleDeletedOldFiles(OldFileData: any) {
    switch (OldFileData.controlName) {
      case 'SustainabilityFileControl': {
        this.deletedOldSustainabilityFilesIds.push(OldFileData?.file?.id);
        this.oldSustainabilityFiles = this.oldSustainabilityFiles.filter(
          (file) => file.id !== OldFileData?.file?.id
        );
        break;
      }
      case 'SustainabilityInitiativesFileControl': {
        this.deletedOldSustainabilityInitiativesFilesIds.push(
          OldFileData?.file?.id
        );
        this.oldSustainabilityInitiativesFiles =
          this.oldSustainabilityInitiativesFiles.filter(
            (file) => file.id !== OldFileData?.file?.id
          );
        break;
      }
      case 'SocialValuesFileControl': {
        this.deletedOldSocialValuesFilesIds.push(OldFileData?.file?.id);
        this.oldSocialValuesFiles = this.oldSocialValuesFiles.filter(
          (file) => file.id !== OldFileData?.file?.id
        );
        break;
      }
      case 'CorporateResponsibilityFileControl': {
        this.deletedOldCorporateResponsibilityFilesIds.push(
          OldFileData?.file?.id
        );
        this.oldCorporateResponsibilityFiles =
          this.oldCorporateResponsibilityFiles.filter(
            (file) => file.id !== OldFileData?.file?.id
          );
        break;
      }
      default:
        break;
    }
  }

  /**
   * Handling File Operations - END
   */
}
